import type { ChatImage } from '@/lib/api'

/** Why an uncertain send is still in the queue. Generic may be proved by history; the other two must not. */
export type DeliveryRecoveryReason = 'generic' | 'client_request_conflict' | 'send_recovery_required'

export function isWithheldRecoveryReason(reason: DeliveryRecoveryReason | undefined): boolean {
  return reason === 'client_request_conflict' || reason === 'send_recovery_required'
}

export interface StoredChatQueueItem {
  id: string
  clientRequestId?: string
  content: string
  images?: Array<ChatImage>
  captureUrl?: string
  stimulusVideos?: Array<{ url: string; label?: string; mimeType?: string }>
  deliveryState?: 'queued' | 'uncertain' | 'blocked'
  recoveryReason?: DeliveryRecoveryReason
}

const PREFIX = 'conversation-queue:v1:'
const memory = new Map<string, Array<StoredChatQueueItem>>()
const listeners = new Map<string, Set<() => void>>()
const failedWrites = new Set<string>()

export function hasChatQueueStorageFailure(key: string | null): boolean {
  return key !== null && failedWrites.has(key)
}

function isStoredItem(value: unknown): value is StoredChatQueueItem {
  if (!value || typeof value !== 'object') return false
  const item = value as Record<string, unknown>
  const optionalString = (value: unknown) => value === undefined || typeof value === 'string'
  return typeof item.id === 'string' && typeof item.content === 'string' &&
    typeof item.clientRequestId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(item.clientRequestId) &&
    ['queued', 'uncertain', 'blocked'].includes(String(item.deliveryState)) &&
    (item.recoveryReason === undefined || item.recoveryReason === 'generic' || isWithheldRecoveryReason(item.recoveryReason as DeliveryRecoveryReason)) &&
    optionalString(item.captureUrl) &&
    (item.images === undefined || (Array.isArray(item.images) && item.images.every(image =>
      image && typeof image.base64 === 'string' && typeof image.mimeType === 'string'))) &&
    (item.stimulusVideos === undefined || (Array.isArray(item.stimulusVideos) && item.stimulusVideos.every(video =>
      video && typeof video.url === 'string' && optionalString(video.label) && optionalString(video.mimeType))))
}

function cloneItems(items: Array<StoredChatQueueItem>) {
  return items.map(item => ({ ...item,
    ...(item.images ? { images: item.images.map(image => ({ ...image })) } : {}),
    ...(item.stimulusVideos ? { stimulusVideos: item.stimulusVideos.map(video => ({ ...video })) } : {}),
  }))
}

export function subscribeChatQueue(key: string | null, listener: () => void) {
  if (!key) return () => {}
  const callbacks = listeners.get(key) ?? new Set<() => void>()
  callbacks.add(listener)
  listeners.set(key, callbacks)
  return () => { callbacks.delete(listener); if (!callbacks.size) listeners.delete(key) }
}

export function chatQueueKey(viewer: string | null, scope: string, room: string | undefined): string | null {
  return viewer && room ? `${PREFIX}${JSON.stringify([viewer, scope, room])}` : null
}

export function loadChatQueue(key: string | null): Array<StoredChatQueueItem> {
  if (!key) return []
  if (memory.has(key)) return cloneItems(memory.get(key)!)
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(key) ?? '[]')
    if (!Array.isArray(parsed) || !parsed.every(isStoredItem)) return []
    const items = parsed
    memory.set(key, items)
    return cloneItems(items)
  } catch { return [] }
}

/** Memory preserves navigation when quota/private mode prevents reload recovery. */
export function saveChatQueue(key: string | null, items: Array<StoredChatQueueItem>): boolean {
  if (!key) return false
  memory.set(key, cloneItems(items))
  for (const listener of listeners.get(key) ?? []) listener()
  try {
    if (items.length) sessionStorage.setItem(key, JSON.stringify(items))
    else sessionStorage.removeItem(key)
    failedWrites.delete(key)
    return true
  } catch { failedWrites.add(key); return false }
}

export function removeAcceptedChatQueueItem(key: string | null, requestId: string | undefined) {
  if (!key || !requestId) return
  saveChatQueue(key, loadChatQueue(key).filter(item => item.clientRequestId !== requestId))
}

/** Called on identity teardown, including logout propagated from another tab. */
export function clearChatQueues() {
  failedWrites.clear()
  const clearedKeys = new Set([...memory.keys(), ...listeners.keys()])
  memory.clear()
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i)
      if (key?.startsWith(PREFIX)) {
        clearedKeys.add(key)
        sessionStorage.removeItem(key)
      }
    }
  } catch { /* Storage may be unavailable; memory has still been cleared. */ }
  // Tombstone even if browser storage removal failed. Mounted consumers must
  // not re-read sensitive previous-viewer data while auth teardown propagates.
  for (const key of clearedKeys) memory.set(key, [])
  for (const key of clearedKeys) for (const listener of listeners.get(key) ?? []) listener()
}
